import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trophy, Clock, Target, TrendingUp, Medal, Crown, BookOpen, CheckCircle2, Coffee, Bell, BellOff, Share2, Copy, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { resolveAssetUrl } from "@/components/ImageUploader";
import Seo from "@/components/Seo";

const padTwo = (n) => String(n).padStart(2, "0");

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const ms = new Date(target).getTime() - now;
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return { days, hours, minutes, seconds, ended: false };
}

export default function Challenge() {
  const { lang, isRTL } = useI18n();
  const { user } = useAuth();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const { data: activeData, isLoading: chLoading } = useQuery({
    queryKey: ["active-challenge"],
    queryFn: endpoints.getActiveChallenge,
  });
  const ch = activeData?.challenge || null;
  const status = activeData?.status || (ch ? "active" : "none");
  const isUpcoming = status === "upcoming";
  const { data: lb } = useQuery({
    queryKey: ["challenge-leaderboard", ch?.id],
    queryFn: () => endpoints.getLeaderboard(ch.id),
    enabled: !!ch?.id && !isUpcoming,
    refetchInterval: 15000,
  });
  // Countdown target: if upcoming, count down to start_at; else count down to end_at.
  const countdown = useCountdown(isUpcoming ? ch?.start_at : ch?.end_at);
  const cover = resolveAssetUrl(ch?.image_url);
  const isRecipeMode = ch?.submission_mode === "recipe";
  const beanLine = ch ? (isRTL ? (ch.bean_name_ar || "") : (ch.bean_name_en || "")) : "";

  // Check if the current user has already submitted (for recipe-mode challenges)
  const { data: myData } = useQuery({
    queryKey: ["my-challenge-submission", ch?.id, user?.user_id],
    queryFn: () => endpoints.getMyChallengeSubmission(ch.id),
    enabled: !!ch?.id && !!user && isRecipeMode && !isUpcoming,
  });
  const mySub = myData?.submitted ? myData : null;

  // Reminder subscription state (only for upcoming challenges)
  const queryClient = useQueryClient();
  const { data: reminderData } = useQuery({
    queryKey: ["challenge-reminder", ch?.id, user?.user_id],
    queryFn: () => endpoints.getChallengeReminder(ch.id),
    enabled: !!ch?.id && !!user && isUpcoming,
  });
  const isSubscribed = !!reminderData?.subscribed;
  const [reminderBusy, setReminderBusy] = useState(false);
  const toggleReminder = async () => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك أولاً عشان نقدر نراسلك" : "Please sign in so we can email you");
      return;
    }
    if (!(user.email || "").trim()) {
      toast.error(isRTL ? "أضف إيميلك في الملف الشخصي أولاً" : "Add your email in your profile first");
      return;
    }
    setReminderBusy(true);
    try {
      if (isSubscribed) {
        await endpoints.unsetChallengeReminder(ch.id);
        toast.success(isRTL ? "تم إلغاء التذكير" : "Reminder cancelled");
      } else {
        await endpoints.setChallengeReminder(ch.id);
        toast.success(isRTL ? "تمام — راح نراسلك قبل ساعة من البدء 🔔" : "Done — we'll email you 1h before start 🔔");
      }
      queryClient.invalidateQueries({ queryKey: ["challenge-reminder", ch.id, user.user_id] });
    } catch (e) {
      toast.error(isRTL ? "تعذّر حفظ التذكير، حاول مرة ثانية" : "Couldn't save reminder, try again");
    } finally {
      setReminderBusy(false);
    }
  };

  // Share challenge — generates a friendly Arabic message with countdown info
  const shareLink = (typeof window !== "undefined" ? window.location.origin : "") + "/challenge";
  const shareText = (() => {
    if (!ch) return "";
    const title = isRTL ? (ch.title_ar || ch.title_en) : (ch.title_en || ch.title_ar);
    const bean = isRTL ? (ch.bean_name_ar || "") : (ch.bean_name_en || "");
    if (isUpcoming && countdown && !countdown.ended) {
      const cdAr = `${countdown.days > 0 ? countdown.days + "ي " : ""}${padTwo(countdown.hours)}س:${padTwo(countdown.minutes)}د`;
      return isRTL
        ? `🔥 ${title}\nالبطولة الأسبوعية من عبدالله الكاساني\n${bean ? "☕ " + bean + "\n" : ""}⏰ تبدأ خلال ${cdAr} — احجز مكانك:`
        : `🔥 ${title}\nWeekly tournament by Abdullah Cassani${bean ? "\n☕ " + bean : ""}\n⏰ Starts in ${cdAr} — join now:`;
    }
    return isRTL
      ? `🏆 ${title}\nالبطولة الأسبوعية من عبدالله الكاساني${bean ? "\n☕ " + bean : ""}\nشارك بوصفتك:`
      : `🏆 ${title}\nWeekly tournament by Abdullah Cassani${bean ? "\n☕ " + bean : ""}\nSubmit your recipe:`;
  })();
  const shareToWhatsapp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareLink)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareLink)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText + "\n" + shareLink);
      toast.success(isRTL ? "تم نسخ الرابط ✅" : "Link copied ✅");
    } catch (_) {
      toast.error(isRTL ? "تعذّر نسخ الرابط" : "Couldn't copy link");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="challenge-page">
      <Seo
        title={isRTL ? "تحدّي القهوة الأسبوعي" : "Weekly Brew Challenge"}
        description={isRTL
          ? "تحدّي القهوة الأسبوعي لباريستا المختصة العرب — اختبر مهاراتك في الاستخلاص والريشيو، نافس بقية المخمّرين على لوحة الصدارة، وافتح إنجازات حصرية."
          : "Weekly specialty brew challenge — test your extraction skills, climb the leaderboard, unlock achievement badges. Built for Arab barista community."}
        keywords="تحدّي القهوة, تحدي القهوة الأسبوعي, مسابقة قهوة, لوحة صدارة قهوة, brewing challenge, barista contest"
        path="/challenge"
        image={cover || undefined}
      />
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4" /> {isRTL ? "تحدّي الأسبوع" : "Weekly Brew Challenge"}
        </div>
        <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>
          {isRTL ? "ارفع مستواك بمحاولة جديدة كل أسبوع" : "Level up — a fresh challenge every week"}
        </h1>
        <p className="mt-5 text-lg text-[#5C544F] leading-relaxed">
          {isRTL
            ? "خمّر، احسب نسبة الاستخلاص، شارك في التحدي، واصعد على لوحة الصدارة."
            : "Brew, measure your EY, join the challenge, and climb the leaderboard."}
        </p>
      </div>

      {chLoading && <div className="mt-12 text-sm text-[#5C544F]">…</div>}

      {!chLoading && !ch && (
        <div className="mt-12 soft-card rounded-2xl p-10 text-center" data-testid="challenge-empty">
          <Trophy className="w-12 h-12 mx-auto text-[#C86240]/40 mb-3" />
          <h3 className={`${serifClass} text-2xl text-[#2A2421] mb-2`}>
            {isRTL ? "لا يوجد تحدي نشط حالياً" : "No active challenge right now"}
          </h3>
          <p className="text-sm text-[#5C544F]">
            {isRTL ? "تابعنا — تحدي جديد قادم قريباً." : "Stay tuned — a new challenge drops soon."}
          </p>
        </div>
      )}

      {ch && (
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Challenge card */}
          <div className="lg:col-span-7 soft-card rounded-2xl overflow-hidden" data-testid="challenge-card">
            {cover && (
              <div className="aspect-[16/8] bg-[#2A2421]/5 overflow-hidden">
                <img src={cover} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-7 lg:p-9">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#4A5D4E]/10 text-[#4A5D4E] border border-[#4A5D4E]/20">
                  {ch.method}
                </span>
                <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  EY {ch.ey_min}–{ch.ey_max}%
                </span>
              </div>
              <h2 className={`${serifClass} text-3xl text-[#2A2421] mb-3`} data-testid="challenge-title">
                {lang === "ar" ? ch.title_ar : ch.title_en}
              </h2>
              <p className="text-[#5C544F] leading-relaxed mb-6 whitespace-pre-line">
                {lang === "ar" ? ch.description_ar : ch.description_en}
              </p>

              {countdown && !countdown.ended && (
                <div className="mb-6">
                  <div className={`text-[10px] uppercase tracking-[0.25em] mb-2 flex items-center gap-1 ${isUpcoming ? "text-[#C86240]" : "text-[#4A5D4E]"}`}>
                    <Clock className="w-3 h-3" />
                    {isUpcoming
                      ? (isRTL ? "يبدأ التحدّي خلال" : "Starts in")
                      : (isRTL ? "متبقي" : "Ends in")}
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-w-md" data-testid={isUpcoming ? "challenge-starts-countdown" : "challenge-countdown"}>
                    <TimeBox value={padTwo(countdown.days)} label={isRTL ? "يوم" : "Days"} highlight={isUpcoming} />
                    <TimeBox value={padTwo(countdown.hours)} label={isRTL ? "ساعة" : "Hours"} highlight={isUpcoming} />
                    <TimeBox value={padTwo(countdown.minutes)} label={isRTL ? "دقيقة" : "Min"} highlight={isUpcoming} />
                    <TimeBox value={padTwo(countdown.seconds)} label={isRTL ? "ثانية" : "Sec"} highlight={isUpcoming} />
                  </div>
                  {isUpcoming && (
                    <p className="mt-2 text-xs text-[#5C544F]" data-testid="challenge-not-started-note">
                      {isRTL
                        ? "هذا التحدّي مجدول — التفاصيل أمامك، والإرسال يفتح تلقائياً عند بدء التحدّي."
                        : "Scheduled challenge — details below; submissions open automatically at start."}
                    </p>
                  )}
                </div>
              )}

              {beanLine && (
                <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3" data-testid="challenge-bean-line">
                  <span className="text-amber-600 text-xl leading-none">☕</span>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-0.5">
                      {isRTL ? "المحصول الرسمي" : "Official Bean"}
                    </div>
                    <div className="text-sm text-amber-900 font-medium">{beanLine}</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat icon={Target} label={isRTL ? "الهدف" : "Target"} value={`${ch.ey_min}–${ch.ey_max}%`} />
                <Stat icon={TrendingUp} label={isRTL ? "المشاركات" : "Submissions"} value={lb?.total_attempts ?? 0} />
                <Stat icon={Trophy} label={isRTL ? "المشاركون" : "Players"} value={lb?.leaderboard?.length ?? 0} />
              </div>

              {isUpcoming ? (
                <div className="rounded-2xl bg-[#FDF2EE] border border-[#C86240]/30 p-5" data-testid="challenge-not-open-yet">
                  <div className="text-sm font-semibold text-[#C86240] mb-1.5">
                    {isRTL ? "📅 التحدّي لم يبدأ بعد" : "📅 Challenge hasn't opened yet"}
                  </div>
                  <p className="text-xs text-[#5C544F] leading-relaxed mb-3">
                    {isRTL
                      ? "اقرأ تفاصيل التحدّي والمحصول والجائزة من فوق، وزر «شارك وصفتك» راح يظهر تلقائياً لحظة بدء التحدّي."
                      : "Read the details, official bean, and prize above — the «Submit your recipe» button appears the moment the challenge opens."}
                  </p>
                  <Button
                    onClick={toggleReminder}
                    disabled={reminderBusy}
                    variant={isSubscribed ? "outline" : "default"}
                    className={`w-full ${isSubscribed ? "border-[#C86240] text-[#C86240] hover:bg-[#FDF2EE]" : "bg-[#C86240] hover:bg-[#B5573A] text-white"}`}
                    data-testid="challenge-remind-me-btn"
                  >
                    {isSubscribed ? (
                      <><BellOff className="w-4 h-4 mr-2" /> {isRTL ? "إلغاء التذكير" : "Cancel reminder"}</>
                    ) : (
                      <><Bell className="w-4 h-4 mr-2" /> {isRTL ? "ذكّرني قبل البدء بساعة" : "Remind me 1h before start"}</>
                    )}
                  </Button>
                  {isSubscribed && (
                    <p className="mt-2 text-[11px] text-[#4A5D4E] text-center" data-testid="challenge-reminder-confirmed">
                      {isRTL ? "✅ راح يوصلك إيميل قبل ساعة من انطلاق التحدّي" : "✅ We'll email you 1h before the challenge starts"}
                    </p>
                  )}
                  <div className="mt-4 pt-4 border-t border-[#C86240]/15">
                    <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#5C544F] mb-2">
                      <Share2 className="w-3 h-3" />
                      {isRTL ? "شارك التحدّي مع الباريستا" : "Share with fellow baristas"}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={shareToWhatsapp}
                        className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#25D366] hover:bg-[#1FB855] text-white text-xs font-semibold transition-colors"
                        data-testid="challenge-share-whatsapp"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {isRTL ? "واتساب" : "WhatsApp"}
                      </button>
                      <button
                        onClick={shareToTwitter}
                        className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#1A1A1A] hover:bg-[#000] text-white text-xs font-semibold transition-colors"
                        data-testid="challenge-share-twitter"
                      >
                        <Twitter className="w-3.5 h-3.5" />
                        X / {isRTL ? "تويتر" : "Twitter"}
                      </button>
                      <button
                        onClick={copyShareLink}
                        className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#E8DFD3] hover:bg-[#D8CFC3] text-[#2A2421] text-xs font-semibold transition-colors"
                        data-testid="challenge-share-copy"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        {isRTL ? "نسخ" : "Copy"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : isRecipeMode ? (
                <RecipeModeAction
                  ch={ch}
                  user={user}
                  mySub={mySub}
                  isRTL={isRTL}
                />
              ) : (
                <>
                  <Link to="/calculator?tab=extraction">
                    <Button className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white" data-testid="challenge-join-btn">
                      <Trophy className="w-4 h-4 me-2" />
                      {isRTL ? "شارك الآن من الحاسبة" : "Join now from calculator"}
                    </Button>
                  </Link>
                  <p className="mt-3 text-xs text-[#5C544F]" data-testid="challenge-daily-rule">
                    {isRTL
                      ? "محاولة واحدة في اليوم — أوّل ما تحسب نسبة استخلاص ضمن الهدف، تتسجّل تلقائياً (احسب براحتك، لكن نقطة الإرسال هي أول محاولة ناجحة)."
                      : "One attempt per day — your first in-target calculation is auto-submitted (calculate freely, but only the first qualifying result counts)."}
                  </p>
                </>
              )}
              {!user && (
                <p className="mt-2 text-xs text-[#5C544F]">
                  {isRTL ? "سجّل الدخول لتظهر نتيجتك على لوحة الصدارة." : "Sign in so your score lands on the leaderboard."}
                </p>
              )}
            </div>
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-5 soft-card rounded-2xl p-6 lg:p-8" data-testid="challenge-leaderboard">
            <div className="flex items-center gap-2 mb-5">
              <Crown className="w-5 h-5 text-[#C86240]" />
              <h3 className={`${serifClass} text-xl text-[#2A2421]`}>
                {isRTL ? "لوحة الصدارة" : "Leaderboard"}
              </h3>
            </div>
            {isUpcoming ? (
              <div className="text-center py-12 text-sm text-[#5C544F]" data-testid="leaderboard-upcoming">
                {isRTL
                  ? "ستفتح لوحة الصدارة فور بدء التحدّي — استعد للمشاركة."
                  : "The leaderboard opens the moment the challenge starts. Get ready."}
              </div>
            ) : !lb || lb.leaderboard.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#5C544F]">
                {isRTL ? "كن أول مشارك! النتائج تظهر فور إرسال محاولتك." : "Be the first! Your score appears as soon as you submit."}
              </div>
            ) : (
              <ul className="space-y-2">
                {lb.leaderboard.map((row, i) => (
                  <li key={row.user_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F9F6F0]" data-testid={`lb-row-${i}`}>
                    <div className="w-7 text-center">
                      {i === 0 ? <Medal className="w-5 h-5 mx-auto text-amber-500" />
                        : i === 1 ? <Medal className="w-5 h-5 mx-auto text-stone-400" />
                          : i === 2 ? <Medal className="w-5 h-5 mx-auto text-amber-700" />
                            : <span className="text-xs font-semibold text-[#5C544F]">{i + 1}</span>}
                    </div>
                    {row.user_picture ? (
                      <img src={row.user_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#C86240]/20 flex items-center justify-center text-xs font-bold text-[#C86240]">
                        {(row.user_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#2A2421] truncate">{row.user_name || "—"}</div>
                      <div className="text-[10px] text-[#5C544F]">
                        {row.attempts} {isRTL ? "محاولة" : "attempts"}{row.best_ey != null ? ` · ${isRTL ? "أفضل" : "best"} EY ${row.best_ey}%` : ""}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-xl font-bold text-[#C86240]">{row.total_score}</div>
                      <div className="text-[9px] uppercase tracking-widest text-[#5C544F]">{isRTL ? "نقاط" : "pts"}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TimeBox = ({ value, label, highlight }) => (
  <div className={`rounded-xl p-2 text-center ${highlight ? "bg-[#C86240] text-white" : "bg-[#2A2421] text-[#F9F6F0]"}`}>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
  </div>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-[#F9F6F0] rounded-xl p-3 text-center">
    <Icon className="w-4 h-4 mx-auto text-[#4A5D4E] mb-1" />
    <div className="text-lg font-bold text-[#2A2421]">{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-[#5C544F]">{label}</div>
  </div>
);

function RecipeModeAction({ ch, user, mySub, isRTL }) {
  // Three states: not signed in / already submitted / can submit
  if (!user) {
    return (
      <Link to={`/login?next=${encodeURIComponent(`/recipes/new?challenge=${ch.id}`)}`}>
        <Button className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white" data-testid="challenge-signin-to-submit-btn">
          <BookOpen className="w-4 h-4 me-2" />
          {isRTL ? "سجّل الدخول لتشارك بوصفتك" : "Sign in to submit your recipe"}
        </Button>
        <p className="mt-3 text-xs text-[#5C544F]" data-testid="challenge-tournament-rule">
          {isRTL
            ? "⚠️ محاولة واحدة فقط طوال البطولة — اختر بعناية. الفائز يحصل على نصف كيلو من نفس المحصول."
            : "⚠️ ONE attempt for the whole tournament — choose carefully. Winner gets 500g of the same bean."}
        </p>
      </Link>
    );
  }
  if (mySub) {
    const r = mySub.recipe;
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="challenge-my-submission">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-emerald-900 mb-1">
              {isRTL ? "وصفتك مُسجّلة في البطولة ✅" : "Your recipe is in the tournament ✅"}
            </div>
            {r && (
              <div className="text-sm text-emerald-800 mb-2">
                <span className="font-medium">{isRTL ? r.title_ar : r.title_en}</span>
              </div>
            )}
            <p className="text-xs text-emerald-800/80 leading-relaxed">
              {isRTL
                ? "عبدالله راح يحضّر وصفتك بنفسه على بثّ Instagram المباشر ويذوّقها. تابع المنصّة لمعرفة موعد البثّ."
                : "Abdullah will brew your recipe live on Instagram and taste it. Watch the platform for the live date."}
            </p>
            {r && (
              <Link
                to={`/recipes?recipe=${encodeURIComponent(r.id)}`}
                className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-emerald-700 hover:underline"
                data-testid="view-my-submitted-recipe-link"
              >
                <BookOpen className="w-3 h-3" />
                {isRTL ? "افتح وصفتك" : "Open your recipe"}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <Link to={`/recipes/new?challenge=${ch.id}`}>
        <Button className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white" data-testid="challenge-submit-recipe-btn">
          <Coffee className="w-4 h-4 me-2" />
          {isRTL ? "شارك وصفتك الكاملة" : "Submit your full recipe"}
        </Button>
      </Link>
      <p className="mt-3 text-xs text-[#5C544F] leading-relaxed" data-testid="challenge-tournament-rule">
        {isRTL
          ? "⚠️ محاولة واحدة فقط طوال البطولة (مو في اليوم — في كل البطولة). الفائز الوحيد يحصل على نصف كيلو من نفس المحصول."
          : "⚠️ ONE attempt for the whole tournament (not per day — the whole tournament). Single winner gets 500g of the same bean."}
      </p>
    </>
  );
}
